import { useState, useRef, useEffect, useCallback, useContext } from 'react';
import { useRouter } from 'next/router';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import {
  SearchBar,
  FooterActions,
  FullPageCartItem,
  CartSkeleton,
  Heading,
  HeadingEnum,
  BoldEnum,
  OffersSnackbar,
  Text,
  TextEnum,
  Image,
  ImageSizeEnum,
} from '@desquared/wave-components-library';
import { useTranslations } from 'use-intl';
import { productsForOrder, fullCartStore } from '@/store/cart';
import { BundleOfferSnackBarOpen } from '@/store/bundleOfferSnackBar';
import { SelectedBundleOfferStore } from '@/store/selectedBundleOfferId';
import { getBundleOfferDescription } from '@/utils/products';
import { BundleOfferSideSheetStore } from '@/store/sideSheet';
import { getSearchResultsV31, getSuggestionsV31 } from '@/api/Search';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { formatProductToOrderTile, addProduct, removeProduct, transformProduct } from '@/utils/products';
import { checkOrderEdit, setCartProducts as setterOfProducts, setDiscounts } from '@/utils/products/products';
import useBundleOffersSummary from '@/hooks/useBundleOffersSummary';
import useDeliveryServices from '@/hooks/useDeliveryServices';
import useProductTags from '@/hooks/useProductTags';

import styles from './SearchProductAside.module.css';
import { CurrencyContext } from '@/contexts/CurrencyProvider';
import { usePersonalizedProductsIds } from '@/contexts/PersonalizedProductsIdsContext.jsx';

export function SearchProductAside({ onProductSelect, selectedOrder, onSelectCheckout, onBack, isAddOnly = false }) {
  const { formatter } = useContext(CurrencyContext);
  const router = useRouter();
  const [query, setQuery] = useState('');
  const [products, setProducts] = useState([]);
  const [cartProducts, setCartProducts] = useState();
  const [suggestedProducts, setSuggestedProducts] = useState([]);
  const [productsAdded, setProductsAdded] = useState(selectedOrder?.products.length);
  const [snackBarOpen, setSnackBarOpen] = useState(false);
  const { bundleOffersSummary } = useBundleOffersSummary();
  const { getCollectionTypes } = useDeliveryServices();
  const [collectionTypes, _] = useState(getCollectionTypes());
  const [disabled, setDisabled] = useState(true);
  const debouncedQuery = useDebouncedValue(query, 500);
  const observer = useRef(null);
  const t = useTranslations();
  const a = useTranslations('common.asides');
  const [data, setData] = useState(selectedOrder);
  const finalSumDescription = t('common.asides.side_cart.final_add_sum');
  const originalSumDescription = t('common.asides.side_cart.original_sum');
  const originalSum = formatter.format(selectedOrder?.totalPrice / 100);
  const { productTags } = useProductTags();
  const { personalizedProductsIds } = usePersonalizedProductsIds();

  useEffect(() => {
    const productsForOrder$ = productsForOrder.subscribe(setCartProducts);

    return () => {
      productsForOrder$.unsubscribe();
    };
  }, []);

  useEffect(() => {
    const BundleOfferSnackBarOpen$ = BundleOfferSnackBarOpen.subscribe(setSnackBarOpen);

    return () => {
      BundleOfferSnackBarOpen$.unsubscribe();
    };
  }, []);

  const {
    data: searchResultsPages,
    isLoading: isLoadingCatalogueProducts,
    isFetchingNextPage,
    fetchNextPage,
  } = useInfiniteQuery({
    queryKey: ['search-results', debouncedQuery, collectionTypes, router.locale],
    enabled: !!debouncedQuery,
    initialPageParam: 0,
    queryFn: ({ queryKey, pageParam }) =>
      getSearchResultsV31({
        term: queryKey[1],
        collectionTypes: collectionTypes.length ? collectionTypes : ['Common'],
        pageNumber: pageParam.toString(),
        pageSize: 10,
      }),
    getNextPageParam: (lastPage, pages) => {
      if (lastPage.pageNumber + 1 >= lastPage.totalPages) return undefined;

      return lastPage.pageNumber + 1;
    },
    staleTime: Infinity,
  });

  const { data: suggestions, isLoading: isLoadingSuggestions } = useQuery({
    queryKey: ['search-suggestions', debouncedQuery, collectionTypes, router.locale],
    enabled: !!debouncedQuery,
    queryFn: ({ queryKey }) => getSuggestionsV31(queryKey[1], collectionTypes.length ? collectionTypes : ['Common']),
  });

  useEffect(() => {
    if (!suggestions) return;

    setSuggestedProducts(
      suggestions.suggestedProducts?.map(product =>
        formatProductToOrderTile(product, bundleOffersSummary, isAddOnly ? selectedOrder : [], a)
      )
    );
  }, [suggestions, bundleOffersSummary, cartProducts]);

  useEffect(() => {
    if (!searchResultsPages) return;

    setProducts(
      searchResultsPages.pages
        .flatMap(page => page.data?.products)
        .map(product => formatProductToOrderTile(product, bundleOffersSummary, isAddOnly ? selectedOrder : [], a))
    );
  }, [searchResultsPages, bundleOffersSummary, cartProducts]);

  const lastResultItemRef = useCallback(
    node => {
      if (!node) return;
      if (isLoadingCatalogueProducts || isFetchingNextPage) return;
      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver(entries => {
        if (entries[0].isIntersecting) {
          fetchNextPage();
        }
      });

      observer.current.observe(node);
    },
    [isFetchingNextPage, isLoadingCatalogueProducts, fetchNextPage]
  );

  const handleProductOnChange = (productType, id) => {
    let originalApiProduct, proxyProduct;

    if (productType === 'catalogue') {
      originalApiProduct = searchResultsPages.pages.flatMap(page => page.data.products).find(p => p._id === id);
      proxyProduct = products.find(p => p._id === id);
    }

    if (productType === 'suggestions') {
      originalApiProduct = suggestions.suggestedProducts.find(p => p._id === id);
      proxyProduct = suggestedProducts?.find(p => p._id === id);
    }

    addProduct(
      transformProduct(
        originalApiProduct,
        null,
        undefined,
        productTags,
        bundleOffersSummary,
        undefined,
        undefined,
        undefined,
        personalizedProductsIds,
        a
      )
    );
  };

  const handleProductOnRemove = (productType, id) => {
    let originalApiProduct, proxyProduct;

    if (productType === 'catalogue') {
      originalApiProduct = searchResultsPages.pages.flatMap(page => page.data.products).find(p => p._id === id);
      proxyProduct = products.find(p => p._id === id);
    }

    if (productType === 'suggestions') {
      originalApiProduct = suggestions.suggestedProducts.find(p => p._id === id);
      proxyProduct = suggestedProducts?.find(p => p._id === id);
    }

    removeProduct(
      transformProduct(
        originalApiProduct,
        null,
        undefined,
        productTags,
        bundleOffersSummary,
        undefined,
        undefined,
        undefined,
        personalizedProductsIds,
        a
      )
    );
  };
  const onBackToCart = () => {
    onBack();
  };

  const continueToCheckout = () => {
    const temp = setterOfProducts(cartProducts.filter(el => !el.productId));

    checkOrderEdit(
      selectedOrder?._id,
      temp,
      selectedOrder,
      bundleOffersSummary,
      true,
      undefined,
      undefined,
      t('full_page_cart.no_comment'),
      undefined,
      a
    )
      .then(result => {
        if (result && result.data) {
          fullCartStore.update(result);
        }
      })
      .finally(() => {
        onSelectCheckout('UPDATE_DETAILS', false);
      });
  };

  useEffect(() => {
    let temp;
    const productsForOrder$ = productsForOrder.subscribe((productsOrder = []) => {
      if (isAddOnly) {
        temp = setterOfProducts(productsOrder);
      } else {
        temp = setterOfProducts(productsOrder);
      }
    });

    if (selectedOrder) {
      checkOrderEdit(
        selectedOrder._id,
        temp,
        selectedOrder,
        bundleOffersSummary,
        isAddOnly,
        undefined,
        undefined,
        t('full_page_cart.no_comment'),
        undefined,
        a
      ).then(result => {
        if (result && result.data) {
          if (result?.data?.order?.products.length > 0) {
            setData(result?.data?.order);
            setProductsAdded(result.data?.order?.products.length);
          }

          if (result?.progress) {
            const { data } = result?.progress;

            setDisabled(data.remainingAmount > 0);
          } else {
            setDisabled(false);
          }
        }
      });
    }

    return () => {
      productsForOrder$.unsubscribe();
    };
  }, [cartProducts]);
  return (
    <>
      <section className={styles.container}>
        <div className={styles.searchBarContainer}>
          <SearchBar
            isRound
            hasContainer
            text={t('common.asides.search_product.search_placeholder')}
            classOption={styles.searchBar}
            value={query}
            setValue={setQuery}
          />
        </div>

        <section className={styles.mainContainer}>
          {isLoadingCatalogueProducts || isLoadingSuggestions ? (
            Array.from({ length: 7 }).map((_, i) => <CartSkeleton key={i} />)
          ) : !products.length && !suggestedProducts?.length ? (
            <div className={styles.empty}>
              <Image
                className={styles.emptyImage}
                size={ImageSizeEnum.EmptyBag}
                imgSrc={process.env.NEXT_PUBLIC_HOSTNAME + 'assets/wave-grocery/EmptyBag.png'}
                imgTitle={'empty-state'}
                alt={'Empty State'}
              />
              <Heading bold={BoldEnum.Bold} type={HeadingEnum.HeadingL}>
                {t('common.asides.search_product.empty_title')}
              </Heading>
              <Text type={TextEnum.Body}>{t('common.asides.search_product.empty_text')}</Text>
            </div>
          ) : null}

          {suggestedProducts?.length ? (
            <>
              <Heading type={HeadingEnum.HeadingS} bold={BoldEnum.Bold}>
                {t('common.asides.search_product.suggested_title')}
              </Heading>
              {suggestedProducts?.map((product, index) => (
                <div key={`${product._id}-${index}`} ref={index === products.length - 1 ? lastResultItemRef : null}>
                  <FullPageCartItem
                    className={styles.fullPageCartItemContainer}
                    {...product}
                    onChange={() => {
                      handleProductOnChange('suggestions', product._id);
                    }}
                    onRemove={() => {
                      handleProductOnRemove('suggestions', product._id);
                    }}
                    onOpenDetails={() => {
                      if (isAddOnly) {
                        onProductSelect(product._id, product.finalPrice, product.beginPrice);
                      } else {
                        onProductSelect(product._id);
                      }
                    }}
                  />
                </div>
              ))}
            </>
          ) : null}

          {products.length ? (
            <>
              <Heading type={HeadingEnum.HeadingS} bold={BoldEnum.Bold}>
                {t('common.asides.search_product.catalogue_products')}
              </Heading>
              {products.map((product, index) => (
                <div
                  className={styles.fullPageCartItemContainer}
                  key={`${product._id}-${index}`}
                  ref={index === products.length - 1 ? lastResultItemRef : null}
                >
                  <FullPageCartItem
                    {...product}
                    onChange={() => {
                      handleProductOnChange('catalogue', product._id);
                    }}
                    onRemove={() => {
                      handleProductOnRemove('catalogue', product._id);
                    }}
                    onOpenDetails={() => {
                      if (isAddOnly) {
                        onProductSelect(product._id, product.finalPrice, product.beginPrice);
                      } else {
                        onProductSelect(product._id);
                      }
                    }}
                  />
                </div>
              ))}
            </>
          ) : null}

          <OffersSnackbar
            emoji="🎉"
            className={styles.snackbar}
            open={snackBarOpen}
            setOpen={BundleOfferSnackBarOpen.update}
            hasCloseOption={true}
            type="warning"
            autoWidth={true}
            buttonText={t('common.offers_snack_bar.btn_label')}
            text={getBundleOfferDescription(SelectedBundleOfferStore.getValue(), bundleOffersSummary)}
            onButtonPress={() => {
              setSnackBarOpen(false);
              BundleOfferSnackBarOpen.update(false);
              BundleOfferSideSheetStore.update(true);
            }}
            autoHideDuration={8000}
            closeOnClickAway={true}
            hasShadow={true}
          />

          {isFetchingNextPage ? <CartSkeleton /> : null}
        </section>

        {products.length || isAddOnly ? (
          <FooterActions
            subtitleRow={
              productsForOrder.getValue().filter(el => !el.productId).length > 0 && {
                description: originalSumDescription,
                value: originalSum,
              }
            }
            titleRow={{
              description: finalSumDescription,
              value:
                data?.totalPrice && productsForOrder.getValue().filter(el => !el.productId).length > 0
                  ? (data?.totalPrice / 100).toFixed(2) + ' €'
                  : originalSum,
            }}
            actions={[
              {
                btnLabel: isAddOnly
                  ? t('common.asides.side_cart.continue_checkout')
                  : t('common.asides.side_cart.return_to_cart'),
                onClick: () => (isAddOnly ? continueToCheckout() : onBackToCart()),
                disabled: isAddOnly ? productsForOrder.getValue().filter(el => !el.productId).length <= 0 : false,
              },
            ]}
          />
        ) : null}
      </section>
    </>
  );
}
